<script setup lang="ts">
import { debounce, Selector } from '@metapic-apps/shared'
import { computed, ref } from 'vue'

import { useAdvertiserGroupsRequest } from '@/api'
import type { FilterType } from '@/lib/types/common.types'

const props = withDefaults(
  defineProps<{
    isReturnAdEnabled: boolean
    shouldTruncateText?: boolean
    label?: string
    size?: 'default' | 'large'
    isDisabled?: boolean
  }>(),
  { size: 'default', isDisabled: false, label: 'Advertiser group' },
)
const emits = defineEmits(['update:advertiserGroup'])
const advertiserGroup = defineModel<FilterType['advertiserGroup']>('advertiserGroup')
const query = ref('')
const { data, isLoading } = useAdvertiserGroupsRequest(props.isReturnAdEnabled)

const advertiserGroupOptions = computed(() =>
  data.value
    .filter((item) => item.name.toLowerCase().includes(query.value.toLowerCase()))
    .map((item) => ({ id: item.id, label: item.name })),
)

const selectedOption = computed(() => (advertiserGroup.value ? { id: advertiserGroup.value.id, label: advertiserGroup.value.name } : null))

const advertiserGroupLabel = computed(() => {
  if (advertiserGroup.value) {
    const { id, name } = advertiserGroup.value
    return name || data.value.find((item) => item.id === id)?.name
  }
  return undefined
})

const handleSearchQuery = debounce((value: string) => (query.value = value), 300)

const handleSelection = (item: { id: number; label?: string } | null) => {
  emits('update:advertiserGroup', item ? { id: item.id, name: item.label ?? '' } : undefined)
}

const handleVisible = () => (query.value = '')
</script>

<template>
  <Selector
    :size="size"
    :is-disabled="isDisabled"
    :options="advertiserGroupOptions"
    :selected-option="selectedOption"
    :selected-label="advertiserGroupLabel"
    :label="label"
    placeholder="Search advertiser groups"
    :loading="isLoading"
    show-clear-button
    :class="['advertiser-group-selector', { 'advertiser-group-selector--truncate-text': shouldTruncateText }]"
    :should-truncate-text="shouldTruncateText"
    :query="query"
    @search="handleSearchQuery"
    @update:selected-option="handleSelection"
    @visible-change="handleVisible"
  />
</template>
<style lang="scss" scoped>
.advertiser-group-selector--truncate-text :deep(.selector-label) {
  max-width: 20ch;
}
</style>
